/*
 * Copyright 2025-2026 Mehr Guard Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

package com.raouf.mehrguard.engine

import com.raouf.mehrguard.core.UrlAnalyzer
import com.raouf.mehrguard.model.ReasonCode

/**
 * Heuristics Engine for Mehr Guard
 *
 * Applies 17+ security heuristics to detect phishing indicators in URLs.
 * Each heuristic is assigned a weight based on its predictive power for phishing.
 *
 * ## Heuristic Categories
 *
 * | Category | Heuristics | Weight Range |
 * |----------|------------|--------------|
 * | Protocol | HTTP (no TLS) | 30 |
 * | Host | IP address, shortener, subdomains, port, entropy | 10-50 |
 * | Path | Keywords, extensions, double extensions | 10-40 |
 * | Query | Credentials, encoded payloads | 30-40 |
 * | Obfuscation | @ injection, punycode, excessive encoding | 20-60 |
 *
 * ## Weighting Rationale
 *
 * Weights are empirically tuned based on:
 * 1. **False positive rates** - Lower weights for ambiguous signals
 * 2. **Attack severity** - Higher weights for confirmed attack patterns
 * 3. **Frequency in phishing** - From PhishTank/OpenPhish analysis
 *
 * ### Critical Weights (40-60)
 * - `IP_ADDRESS_HOST (50)`: Rarely legitimate, often phishing infrastructure
 * - `AT_SYMBOL_INJECTION (60)`: Almost always malicious URL spoofing
 * - `CREDENTIAL_PARAMS (40)`: Strong phishing indicator
 * - `RISKY_EXTENSION (40)`: Malware delivery via file extensions
 *
 * ### Moderate Weights (15-30)
 * - `HTTP_NOT_HTTPS (30)`: Serious for sensitive pages
 * - `PUNYCODE_DOMAIN (30)`: Homograph attack vector
 * - `ENTROPY (20)`: DGA domains, but some legit CDNs
 *
 * ### Low Weights (5-15)
 * - `SHORTENER (15)`: Legitimate use cases exist
 * - `LONG_URL (10)`: Marketing URLs can be long
 * - `SUBDOMAINS (10)`: Some legit services use many
 *
 * ## Security Notes
 *
 * - All inputs are validated before processing (max 2048 chars)
 * - Regex patterns avoid catastrophic backtracking (O(n) max)
 * - Score calculations use safe arithmetic with 0-100 bounds
 * - Thread-safe: no mutable state
 *
 * @property urlAnalyzer The URL parser for extracting components
 * @author Mehr Guard Security Team
 * @since 1.0.0
 * @see com.raouf.mehrguard.core.SecurityConstants for threshold values
 */
class HeuristicsEngine(
    private val urlAnalyzer: UrlAnalyzer = UrlAnalyzer()
) {

    /**
     * Heuristic analysis result.
     */
    data class Result(
        val score: Int,
        val flags: List<String>,
        val details: Map<String, Int>,
        /** Stable reason codes for explainability */
        val reasons: List<ReasonCode> = emptyList()
    ) {
        companion object {
            /** Result for unparseable URLs */
            val UNPARSEABLE = Result(
                score = 50,
                flags = listOf("Unable to parse URL"),
                details = emptyMap(),
                reasons = listOf(ReasonCode.REASON_UNPARSEABLE)
            )
        }
    }

    /**
     * Individual heuristic check.
     */
    private data class HeuristicCheck(
        val name: String,
        val weight: Int,
        val message: String,
        val reasonCode: ReasonCode
    )

    /**
     * Analyze URL using security heuristics.
     *
     * @param url The URL to analyze
     * @return Result containing score (0-100), flags, and details
     */
    fun analyze(url: String): Result {
        // SECURITY: Validate input length
        if (url.length > UrlAnalyzer.MAX_URL_LENGTH) {
            return Result(
                score = 60,
                flags = listOf("URL exceeds maximum safe length"),
                details = mapOf("URL_TOO_LONG" to 60)
            )
        }

        val parsed = urlAnalyzer.parse(url) ?: return Result.UNPARSEABLE

        val checks = mutableListOf<HeuristicCheck>()

        // === PROTOCOL CHECKS ===

        // 1. HTTP vs HTTPS
        if (!urlAnalyzer.isSecure(parsed)) {
            checks.add(HeuristicCheck(
                "HTTP_NOT_HTTPS",
                WEIGHT_HTTP_NOT_HTTPS,
                "Uses insecure HTTP protocol",
                ReasonCode.REASON_HTTP_NOT_HTTPS
            ))
        }

        // === HOST CHECKS ===

        // 2. IP address as host
        if (urlAnalyzer.isIpAddress(parsed.host)) {
            checks.add(HeuristicCheck(
                "IP_ADDRESS_HOST",
                WEIGHT_IP_ADDRESS,
                "Host is an IP address instead of domain",
                ReasonCode.REASON_IP_HOST
            ))
        }

        // 3. URL shortener detection
        if (urlAnalyzer.isShortener(parsed.host)) {
            checks.add(HeuristicCheck(
                "URL_SHORTENER",
                WEIGHT_SHORTENER,
                "Uses URL shortening service",
                ReasonCode.REASON_URL_SHORTENER
            ))
        }

        // 4. Excessive subdomains
        val subdomainCount = parsed.subdomains.size
        if (subdomainCount > 3) {
            checks.add(HeuristicCheck(
                "EXCESSIVE_SUBDOMAINS",
                WEIGHT_SUBDOMAINS,
                "Excessive subdomain depth ($subdomainCount levels)",
                ReasonCode.REASON_DEEP_SUBDOMAIN
            ))
        }

        // 5. Non-standard port
        if (parsed.port != null && parsed.port !in STANDARD_PORTS) {
            checks.add(HeuristicCheck(
                "NON_STANDARD_PORT",
                WEIGHT_PORT,
                "Non-standard port: ${parsed.port}",
                ReasonCode.REASON_NON_STANDARD_PORT
            ))
        }

        // === URL STRUCTURE CHECKS ===

        // 6. Long URL (refined to not penalize legitimate marketing URLs)
        if (url.length > 250) {
            // Check if this is likely a legitimate marketing URL with UTM params
            val hasUtmParams = parsed.query?.lowercase()?.let { q ->
                q.contains("utm_") || q.contains("campaign=") || q.contains("source=")
            } ?: false

            // Marketing URLs with UTM params are less suspicious
            val longUrlWeight = if (hasUtmParams && url.length < 400) {
                2 // Minimal penalty for marketing URLs
            } else {
                WEIGHT_LONG_URL
            }

            checks.add(HeuristicCheck(
                "LONG_URL",
                longUrlWeight,
                "Unusually long URL (${url.length} characters)" +
                    if (hasUtmParams) " - contains marketing parameters" else "",
                ReasonCode.REASON_LONG_URL
            ))
        }

        // 7. High entropy domain (suggests randomized DGA)
        val hostEntropy = urlAnalyzer.calculateEntropy(parsed.host)
        if (hostEntropy > ENTROPY_THRESHOLD) {
            checks.add(HeuristicCheck(
                "HIGH_ENTROPY_HOST",
                WEIGHT_ENTROPY,
                "High randomness in domain name",
                ReasonCode.REASON_HIGH_ENTROPY_HOST
            ))
        }

        // === PATH CHECKS ===

        // 8. Suspicious path keywords
        val suspiciousKeywordCount = urlAnalyzer.countSuspiciousPathKeywords(parsed.path)
        if (suspiciousKeywordCount > 0) {
            val keywordScore = (suspiciousKeywordCount * 5).coerceAtMost(20)
            checks.add(HeuristicCheck(
                "SUSPICIOUS_PATH_KEYWORDS",
                keywordScore,
                "Suspicious keywords in path ($suspiciousKeywordCount found)",
                ReasonCode.REASON_SUSPICIOUS_PATH
            ))
        }

        // === QUERY CHECKS ===

        // 9. Credential parameters
        if (urlAnalyzer.hasCredentialParams(parsed.query)) {
            checks.add(HeuristicCheck(
                "CREDENTIAL_PARAMS",
                WEIGHT_CREDENTIAL_PARAMS,
                "Credential-related parameters in URL",
                ReasonCode.REASON_CREDENTIAL_PARAM
            ))
        }

        // 10. Base64 in query string (potential payload)
        if (hasEncodedPayload(parsed.query)) {
            checks.add(HeuristicCheck(
                "ENCODED_PAYLOAD",
                WEIGHT_BASE64,
                "Encoded data detected in query parameters",
                ReasonCode.REASON_ENCODED_PAYLOAD
            ))
        }

        // === URL OBFUSCATION CHECKS ===

        // 11. @ symbol in URL (user info injection)
        if (hasAtSymbolInjection(url)) {
            checks.add(HeuristicCheck(
                "AT_SYMBOL_INJECTION",
                WEIGHT_AT_SYMBOL,
                "Contains @ symbol (possible URL spoofing)",
                ReasonCode.REASON_AT_SYMBOL_INJECTION
            ))
        }

        // 12. Multiple TLD-like segments
        val tldLikeCount = countTldLikeSegments(parsed.host)
        if (tldLikeCount > 1) {
            checks.add(HeuristicCheck(
                "MULTIPLE_TLD_SEGMENTS",
                WEIGHT_MULTI_TLD,
                "Multiple TLD-like segments in domain",
                ReasonCode.REASON_MULTI_TLD
            ))
        }

        // 13. Punycode/IDN domain
        if (parsed.host.contains("xn--")) {
            checks.add(HeuristicCheck(
                "PUNYCODE_DOMAIN",
                WEIGHT_PUNYCODE,
                "Internationalized domain (potential homograph)",
                ReasonCode.REASON_HOMOGRAPH
            ))
        }

        // 14. Numeric subdomain
        if (hasNumericSubdomain(parsed.subdomains)) {
            checks.add(HeuristicCheck(
                "NUMERIC_SUBDOMAIN",
                WEIGHT_NUMERIC_SUBDOMAIN,
                "Numeric-only subdomain detected",
                ReasonCode.REASON_NUMERIC_SUBDOMAIN
            ))
        }

        // 15. Suspicious file extension
        if (hasRiskyExtension(parsed.path)) {
            checks.add(HeuristicCheck(
                "RISKY_EXTENSION",
                WEIGHT_RISKY_EXTENSION,
                "Potentially dangerous file extension in path",
                ReasonCode.REASON_RISKY_EXTENSION
            ))
        }

        // 16. Double extension (e.g., invoice.pdf.exe)
        if (hasDoubleExtension(parsed.path)) {
            checks.add(HeuristicCheck(
                "DOUBLE_EXTENSION",
                WEIGHT_DOUBLE_EXTENSION,
                "Double file extension detected (common malware tactic)",
                ReasonCode.REASON_DOUBLE_EXTENSION
            ))
        }

        // 17. Hex-encoded characters in path
        if (hasExcessiveEncoding(parsed.path)) {
            checks.add(HeuristicCheck(
                "EXCESSIVE_ENCODING",
                WEIGHT_ENCODING,
                "Excessive URL encoding detected",
                ReasonCode.REASON_EXCESSIVE_ENCODING
            ))
        }

        // === NEW ENHANCED HEURISTICS ===

        // 18. Zero-width character detection
        if (hasZeroWidthCharacters(url)) {
            checks.add(HeuristicCheck(
                "ZERO_WIDTH_CHARS",
                WEIGHT_ZERO_WIDTH,
                "Hidden zero-width Unicode characters detected",
                ReasonCode.REASON_ZERO_WIDTH_CHARS
            ))
        }

        // 19. Data URI scheme detection
        if (isDataUri(url)) {
            checks.add(HeuristicCheck(
                "DATA_URI_SCHEME",
                WEIGHT_DATA_URI,
                "Data URI scheme detected - may contain embedded code",
                ReasonCode.REASON_DATA_URI
            ))
        }

        // 20. JavaScript URL detection
        if (isJavaScriptUrl(url)) {
            checks.add(HeuristicCheck(
                "JAVASCRIPT_URL",
                WEIGHT_JAVASCRIPT_URL,
                "JavaScript URL scheme detected - executable code",
                ReasonCode.REASON_JAVASCRIPT_URL
            ))
        }

        // 21. Fragment parameter hiding (using # to obscure)
        if (hasFragmentHiding(url)) {
            checks.add(HeuristicCheck(
                "FRAGMENT_HIDING",
                WEIGHT_FRAGMENT_HIDING,
                "Suspicious use of URL fragment to hide content",
                ReasonCode.REASON_FRAGMENT_HIDING
            ))
        }

        // 22. Credential harvesting keywords in URL
        val credentialKeywordCount = countCredentialHarvestingKeywords(url)
        if (credentialKeywordCount > 0) {
            val keywordScore = (credentialKeywordCount * WEIGHT_CREDENTIAL_KEYWORD).coerceAtMost(40)
            checks.add(HeuristicCheck(
                "CREDENTIAL_KEYWORDS",
                keywordScore,
                "Credential harvesting keywords detected ($credentialKeywordCount found)",
                ReasonCode.REASON_CREDENTIAL_KEYWORDS
            ))
        }

        // 23. Suspicious port numbers (common attack vectors)
        if (parsed.port != null && parsed.port in SUSPICIOUS_PORTS) {
            checks.add(HeuristicCheck(
                "SUSPICIOUS_PORT",
                WEIGHT_SUSPICIOUS_PORT,
                "Suspicious port number: ${parsed.port}",
                ReasonCode.REASON_SUSPICIOUS_PORT
            ))
        }

        // 24. Lookalike Unicode characters (beyond punycode)
        if (hasLookalikeCharacters(parsed.host)) {
            checks.add(HeuristicCheck(
                "LOOKALIKE_CHARS",
                WEIGHT_LOOKALIKE,
                "Lookalike Unicode characters detected in domain",
                ReasonCode.REASON_LOOKALIKE_CHARS
            ))
        }

        // 25. Domain age simulation (sequential numbers in domain)
        if (hasDomainAgeSimulation(parsed.host)) {
            checks.add(HeuristicCheck(
                "DOMAIN_AGE_SIMULATION",
                WEIGHT_DOMAIN_AGE,
                "Domain appears to be newly generated (number sequences)",
                ReasonCode.REASON_DOMAIN_AGE_PATTERN
            ))
        }

        // Calculate final score with bounds
        val totalScore = checks.sumOf { it.weight }.coerceIn(0, 100)
        val flags = checks.map { it.message }
        val details = checks.associate { it.name to it.weight }
        val reasons = checks.map { it.reasonCode }

        return Result(
            score = totalScore,
            flags = flags,
            details = details,
            reasons = reasons
        )
    }

    // === HELPER METHODS ===

    /**
     * Check for @ symbol injection attack.
     * Example: https://google.com@malicious.com/path
     */
    private fun hasAtSymbolInjection(url: String): Boolean {
        val protocolEnd = url.indexOf("://")
        if (protocolEnd < 0) return false

        val atIndex = url.indexOf('@', protocolEnd + 3)
        val slashIndex = url.indexOf('/', protocolEnd + 3)

        // @ before first path slash indicates user info injection
        return atIndex > 0 && (slashIndex < 0 || atIndex < slashIndex)
    }

    /**
     * Count TLD-like segments in host.
     */
    private fun countTldLikeSegments(host: String): Int {
        val parts = host.split(".")
        return parts.count { it.lowercase() in COMMON_TLDS }
    }

    /**
     * Check for numeric-only subdomains.
     */
    private fun hasNumericSubdomain(subdomains: List<String>): Boolean {
        return subdomains.any { subdomain ->
            subdomain.isNotEmpty() && subdomain.all { it.isDigit() }
        }
    }

    /**
     * Check for risky file extensions.
     */
    private fun hasRiskyExtension(path: String): Boolean {
        val pathLower = path.lowercase()
        return RISKY_EXTENSIONS.any { ext ->
            pathLower.endsWith(ext)
        }
    }

    /**
     * Check for double extension (e.g., file.pdf.exe).
     */
    private fun hasDoubleExtension(path: String): Boolean {
        val filename = path.substringAfterLast('/')
        val extensionCount = filename.count { it == '.' }
        return extensionCount >= 2 && RISKY_EXTENSIONS.any { ext ->
            filename.lowercase().endsWith(ext)
        }
    }

    /**
     * Check for encoded payload in query or potential data exfiltration.
     *
     * Detects:
     * - Base64 encoded data (50+ chars of Base64 alphabet)
     * - Potential credential exfiltration patterns
     * - Data URI payloads
     *
     * Uses safe pattern matching without catastrophic backtracking.
     */
    private fun hasEncodedPayload(query: String?): Boolean {
        if (query == null || query.length < 20) return false

        // Check for data: URI scheme (inline data exfiltration)
        if (query.lowercase().contains("data:")) {
            return true
        }

        // Check for suspicious key names that might contain exfiltrated data
        val exfiltrationKeys = listOf(
            "token", "auth", "session", "credential", "pwd", "password",
            "secret", "apikey", "access_token", "refresh_token", "bearer",
            "payload", "data", "encoded", "b64", "base64"
        )

        val queryLower = query.lowercase()
        val hasExfiltrationKey = exfiltrationKeys.any { key ->
            queryLower.contains("$key=") && getParamValueLength(queryLower, key) > 30
        }

        if (hasExfiltrationKey) return true

        // Count Base64-like characters (alphanumeric + / + = padding)
        var consecutiveBase64 = 0
        var maxConsecutive = 0
        var equalsCount = 0

        for (char in query) {
            if (char.isLetterOrDigit() || char == '+' || char == '/') {
                consecutiveBase64++
                maxConsecutive = maxOf(maxConsecutive, consecutiveBase64)
            } else if (char == '=') {
                equalsCount++
                // Equals at end of Base64 is padding
                if (equalsCount <= 2) {
                    consecutiveBase64++
                    maxConsecutive = maxOf(maxConsecutive, consecutiveBase64)
                }
            } else {
                consecutiveBase64 = 0
                equalsCount = 0
            }
        }

        // Base64 strings are typically at least 50 chars for meaningful data
        return maxConsecutive >= 50
    }

    /**
     * Get the length of a parameter value from a query string.
     */
    private fun getParamValueLength(query: String, key: String): Int {
        val startIndex = query.indexOf("$key=")
        if (startIndex < 0) return 0

        val valueStart = startIndex + key.length + 1
        val valueEnd = query.indexOf('&', valueStart).takeIf { it > 0 } ?: query.length

        return (valueEnd - valueStart).coerceAtLeast(0)
    }

    /**
     * Check for excessive URL encoding (obfuscation).
     */
    private fun hasExcessiveEncoding(path: String): Boolean {
        val percentCount = path.count { it == '%' }
        return percentCount >= 5 && percentCount.toFloat() / path.length > 0.1f
    }

    // === NEW ENHANCED HEURISTIC HELPER METHODS ===

    /**
     * Check for hidden zero-width Unicode characters.
     * These are invisible but can be used to evade detection.
     */
    private fun hasZeroWidthCharacters(url: String): Boolean {
        return url.any { it in ZERO_WIDTH_CHARS }
    }

    /**
     * Check if URL uses data: URI scheme.
     * Data URIs can contain embedded HTML/JavaScript.
     */
    private fun isDataUri(url: String): Boolean {
        val urlLower = url.lowercase().trim()
        return urlLower.startsWith("data:") ||
               urlLower.contains("?data:") ||
               urlLower.contains("&data:")
    }

    /**
     * Check if URL uses javascript: scheme.
     * JavaScript URLs execute code when clicked.
     */
    private fun isJavaScriptUrl(url: String): Boolean {
        val urlLower = url.lowercase().trim()
        return urlLower.startsWith("javascript:") ||
               urlLower.startsWith("vbscript:")
    }

    /**
     * Check for suspicious use of URL fragment to hide content.
     * Fragments after # are not sent to server but can be used client-side.
     */
    private fun hasFragmentHiding(url: String): Boolean {
        val fragmentIndex = url.indexOf('#')
        if (fragmentIndex < 0) return false

        val fragment = url.substring(fragmentIndex + 1)
        // Suspicious if fragment looks like a URL or contains suspicious patterns
        return fragment.contains("://") ||
               fragment.contains("?") ||
               fragment.length > 100 ||
               fragment.contains("base64") ||
               fragment.contains("eval(")
    }

    /**
     * Count credential harvesting keywords in URL.
     * Multiple keywords suggest phishing attempt.
     */
    private fun countCredentialHarvestingKeywords(url: String): Int {
        val urlLower = url.lowercase()
        return CREDENTIAL_KEYWORDS.count { keyword ->
            urlLower.contains(keyword)
        }
    }

    /**
     * Check for lookalike Unicode characters (beyond simple punycode).
     * Detects mathematical symbols and Cyrillic characters used to impersonate.
     */
    private fun hasLookalikeCharacters(host: String): Boolean {
        return host.any { char -> char in LOOKALIKE_CHARS.keys }
    }

    /**
     * Check for domain age simulation patterns.
     * Newly generated domains often have sequential numbers or random sequences.
     */
    private fun hasDomainAgeSimulation(host: String): Boolean {
        // Check for sequential numbers (e.g., domain123456.com)
        val digitSequencePattern = Regex("\\d{5,}")
        if (digitSequencePattern.containsMatchIn(host)) return true

        // Check for alternating letters and numbers (e.g., a1b2c3d4.com)
        val alternatingPattern = Regex("([a-z]\\d){3,}|([\\d][a-z]){3,}", RegexOption.IGNORE_CASE)
        if (alternatingPattern.containsMatchIn(host)) return true

        // Check for random-looking hex sequences (e.g., a8f3c2e1.com)
        val parts = host.split(".")
        for (part in parts) {
            if (part.length >= 8 && part.all { it.isLetterOrDigit() }) {
                val digits = part.count { it.isDigit() }
                val letters = part.count { it.isLetter() }
                // Roughly equal mix suggests random generation
                if (digits >= 3 && letters >= 3 && kotlin.math.abs(digits - letters) <= 2) {
                    return true
                }
            }
        }

        return false
    }

    companion object {
        // === HEURISTIC WEIGHTS ===
        const val WEIGHT_HTTP_NOT_HTTPS = 30  // Increased from 15
        const val WEIGHT_IP_ADDRESS = 50      // Increased from 20
        const val WEIGHT_SHORTENER = 15       // Increased from 8
        const val WEIGHT_SUBDOMAINS = 10
        const val WEIGHT_PORT = 15            // Increased from 8
        const val WEIGHT_LONG_URL = 10        // Increased from 5
        const val WEIGHT_ENTROPY = 20         // Increased from 12
        const val WEIGHT_CREDENTIAL_PARAMS = 40 // Increased from 18
        const val WEIGHT_AT_SYMBOL = 60       // Increased from 15 (Critical risk)
        const val WEIGHT_MULTI_TLD = 25       // Increased from 10
        const val WEIGHT_PUNYCODE = 30        // Increased from 15
        const val WEIGHT_NUMERIC_SUBDOMAIN = 20 // Increased from 8
        const val WEIGHT_RISKY_EXTENSION = 40 // Increased from 25
        const val WEIGHT_DOUBLE_EXTENSION = 40 // Increased from 20
        const val WEIGHT_BASE64 = 30          // Increased from 10
        const val WEIGHT_ENCODING = 20        // Increased from 8

        // === NEW ENHANCED HEURISTIC WEIGHTS ===
        const val WEIGHT_ZERO_WIDTH = 50       // Hidden characters are highly suspicious
        const val WEIGHT_DATA_URI = 60         // Data URIs can contain executable code
        const val WEIGHT_JAVASCRIPT_URL = 70   // JavaScript URLs are almost always malicious
        const val WEIGHT_FRAGMENT_HIDING = 25  // Used to hide real URL
        const val WEIGHT_CREDENTIAL_KEYWORD = 10 // Per keyword, max 40
        const val WEIGHT_SUSPICIOUS_PORT = 25  // Common attack ports
        const val WEIGHT_LOOKALIKE = 35        // Lookalike characters
        const val WEIGHT_DOMAIN_AGE = 20       // Newly generated domains

        // === THRESHOLDS ===
        const val ENTROPY_THRESHOLD = 4.0

        // === REFERENCE DATA ===
        val STANDARD_PORTS = setOf(80, 443, 8080, 8443)

        val SUSPICIOUS_PORTS = setOf(
            4444,  // Metasploit default
            5555,  // Android ADB
            6666,  // IRC/Trojans
            6667,  // IRC
            8888,  // Alternate HTTP often used by phishing
            9999,  // Alternate admin
            1337,  // "leet" - hacker culture
            31337  // "eleet" - backdoor
        )

        val COMMON_TLDS = setOf(
            "com", "org", "net", "edu", "gov", "io", "co", "us", "uk",
            "app", "dev", "xyz", "info", "biz", "me", "tv", "cc"
        )

        val RISKY_EXTENSIONS = setOf(
            ".exe", ".scr", ".bat", ".cmd", ".ps1", ".msi", ".com",
            ".pif", ".vbs", ".vbe", ".js", ".jse", ".ws", ".wsf",
            ".hta", ".cpl", ".msc", ".jar", ".app", ".dmg"
        )

        // Credential harvesting keywords commonly used in phishing
        val CREDENTIAL_KEYWORDS = setOf(
            "verify", "confirm", "update", "secure", "login", "signin",
            "account", "password", "credential", "billing", "payment",
            "suspend", "locked", "unusual", "activity", "expire",
            "validate", "authenticate", "reactivate", "restore"
        )

        // Zero-width Unicode characters often used for obfuscation
        val ZERO_WIDTH_CHARS = setOf(
            '\u200B', // Zero-width space
            '\u200C', // Zero-width non-joiner
            '\u200D', // Zero-width joiner
            '\uFEFF', // Byte order mark
            '\u2060', // Word joiner
            '\u180E'  // Mongolian vowel separator
        )

        // Lookalike Unicode characters (beyond simple homoglyphs)
        val LOOKALIKE_CHARS = mapOf(
            'ℙ' to 'P', 'ℚ' to 'Q', 'ℝ' to 'R', 'ℕ' to 'N', 'ℤ' to 'Z',
            'ℂ' to 'C', 'ℍ' to 'H', 'ℐ' to 'I', 'ℒ' to 'L', 'ℳ' to 'M',
            'ℬ' to 'B', 'ℰ' to 'E', 'ℱ' to 'F', 'ℋ' to 'H', 'ℛ' to 'R',
            'а' to 'a', 'е' to 'e', 'о' to 'o', 'р' to 'p', 'с' to 'c', // Cyrillic
            'х' to 'x', 'у' to 'y', 'і' to 'i', 'ј' to 'j', 'ѕ' to 's'
        )
    }
}

